import { useState, useRef, useMemo } from 'react'
import { FACT_FINDS } from './factFinds'

const GEOAPIFY_KEY = 'a8f19b87ffe24f89b222eda754c1407a'
const ADDRESS_FIELD_KEYS = new Set([
  'ff_business_address',
  'ff_residential_address',
  'ff_property_address',
  'ff_mailing_address',
  'ff_mooring_address',
  'ff_storage_address',
  'ff_project_address',
])

// ── Address Autocomplete (inline — no app.js changes needed) ─────────────────
function AddressField({ val, set, placeholder, highlightStyle }) {
  const [suggestions, setSuggestions] = useState([])
  const [showSuggestions, setShowSuggestions] = useState(false)
  const [searching, setSearching] = useState(false)
  const timerRef = useRef(null)

  const handleChange = (e) => {
    const v = e.target.value
    set(v)
    setShowSuggestions(false)
    setSuggestions([])
    if (timerRef.current) clearTimeout(timerRef.current)
    if (v.length < 3) return
    timerRef.current = setTimeout(async () => {
      setSearching(true)
      try {
        const res = await fetch(`https://api.geoapify.com/v1/geocode/autocomplete?text=${encodeURIComponent(v)}&country=au&limit=6&apiKey=${GEOAPIFY_KEY}`)
        const data = await res.json()
        if (data.features?.length) { setSuggestions(data.features); setShowSuggestions(true) }
      } catch(e) {}
      setSearching(false)
    }, 350)
  }

  const selectSuggestion = (feature) => {
    const p = feature.properties
    const parts = [p.housenumber && p.street ? `${p.housenumber} ${p.street}` : p.street || p.name, p.suburb || p.city, p.state_code || p.state, p.postcode].filter(Boolean)
    set(parts.join(', '))
    setSuggestions([])
    setShowSuggestions(false)
  }

  return (
    <div style={{ position: 'relative' }}>
      <input
        className="ff-input"
        style={{ width: '100%', ...highlightStyle }}
        type="text"
        value={val || ''}
        placeholder={placeholder || 'Start typing address...'}
        onChange={handleChange}
        onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
        autoComplete="off"
      />
      {searching && <div style={{ fontSize: 11, color: '#6b8278', marginTop: 3, paddingLeft: 2 }}>Searching...</div>}
      {showSuggestions && suggestions.length > 0 && (
        <div style={{ position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 300, background: '#fff', border: '1.5px solid rgba(30,61,48,0.15)', borderRadius: 10, boxShadow: '0 8px 24px rgba(0,0,0,0.12)', overflow: 'hidden', marginTop: 4 }}>
          {suggestions.map((f, i) => (
            <div
              key={i}
              onMouseDown={() => selectSuggestion(f)}
              style={{ padding: '10px 14px', borderBottom: '1px solid #e2e8e5', cursor: 'pointer', fontSize: 12, lineHeight: 1.4 }}
              onMouseEnter={e => (e.currentTarget.style.background = 'rgba(30,61,48,0.08)')}
              onMouseLeave={e => (e.currentTarget.style.background = '')}
            >
              <div style={{ fontWeight: 500 }}>{f.properties.formatted}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  )
}

// ── ABN Lookup Field (preserved from original) ────────────────────────────────
function AbnField({ val, set, onChange, highlightStyle }) {
  const [loading, setLoading] = useState(false)
  const [result, setResult] = useState(null)
  const [error, setError] = useState('')

  const handleLookup = async () => {
    if (!val || val.replace(/\s/g, '').length < 11) {
      setError('Enter an 11-digit ABN first')
      return
    }
    setLoading(true)
    setError('')
    setResult(null)
    try {
      const { supabase } = await import('./supabase')
      const { data: { session } } = await supabase.auth.getSession()
      const res = await fetch(`${process.env.REACT_APP_SUPABASE_URL}/functions/v1/abn-lookup`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${session.access_token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ abn: val }),
      })
      const data = await res.json()
      if (!data.success) throw new Error(data.error || 'Lookup failed')
      setResult(data)
      onChange(prev => ({
        ...prev,
        ff_company_name:   prev.ff_company_name || data.mainName,
        ff_entity_name:    data.legalName,
        ff_company_type:   prev.ff_company_type || data.entityType,
        ff_gst_registered: data.gstReg,
      }))
    } catch (e) {
      setError(e.message || 'Lookup failed')
    }
    setLoading(false)
  }

  return (
    <div>
      <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
        <input
          className="ff-input"
          style={{ flex: 1, ...highlightStyle }}
          type="text"
          value={val}
          onChange={e => { set(e.target.value); setResult(null); setError('') }}
          placeholder="e.g. 51 824 753 556"
          maxLength={14}
        />
        <button
          onClick={handleLookup}
          disabled={loading}
          style={{ padding: '8px 16px', borderRadius: 8, background: loading ? '#ccc' : '#1e3d30', color: '#fff', border: 'none', fontSize: 12, fontWeight: 700, cursor: loading ? 'not-allowed' : 'pointer', whiteSpace: 'nowrap', fontFamily: 'var(--font)', flexShrink: 0 }}
        >
          {loading ? '⏳ Looking up...' : '🔍 Lookup ABN'}
        </button>
      </div>

      {error && (
        <div style={{ marginTop: 6, fontSize: 12, color: '#c0450a', background: 'rgba(230,168,23,0.1)', border: '1px solid rgba(230,168,23,0.3)', borderRadius: 6, padding: '5px 10px' }}>
          ⚠️ {error}
        </div>
      )}

      {result && (
        <div style={{ marginTop: 8, background: 'rgba(30,61,48,0.06)', border: '1.5px solid rgba(30,61,48,0.2)', borderRadius: 10, padding: '12px 14px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 8 }}>
            <span style={{ fontSize: 16 }}>✅</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: '#1e3d30' }}>{result.mainName}</span>
            <span style={{ fontSize: 11, padding: '2px 8px', borderRadius: 10, background: result.abnStatus === 'Active' ? 'rgba(30,61,48,0.15)' : 'rgba(220,53,69,0.12)', color: result.abnStatus === 'Active' ? '#1e3d30' : '#dc3545', fontWeight: 700 }}>{result.abnStatus}</span>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 8 }}>
            {[
              ['Entity Type', result.entityType || '—'],
              ['Legal Name', result.legalName || '—'],
              ['GST Registered', result.gstReg],
            ].map(([label, value]) => (
              <div key={label} style={{ background: '#fff', borderRadius: 7, padding: '7px 10px', border: '1px solid rgba(30,61,48,0.12)' }}>
                <div style={{ fontSize: 10, fontWeight: 700, color: '#6b8278', textTransform: 'uppercase', letterSpacing: '.4px', marginBottom: 2 }}>{label}</div>
                <div style={{ fontSize: 12, fontWeight: 600, color: '#1a2e25' }}>{value}</div>
              </div>
            ))}
          </div>
          <div style={{ fontSize: 11, color: '#6b8278', marginTop: 8 }}>✓ Entity name, type and GST status auto-filled into fact find</div>
        </div>
      )}
    </div>
  )
}

// ── Repeatable Truck group field (Registration / Make-Model-Year / GVM / Sum Insured) ──
function TruckGroupField({ val, set, subFields, highlightStyle }) {
  const trucks = (val && val.length > 0) ? val : [{}]

  const updateTruck = (idx, key, v) => {
    set(trucks.map((t, i) => i === idx ? { ...t, [key]: v } : t))
  }
  const addTruck = () => set([...trucks, {}])
  const removeTruck = (idx) => {
    const next = trucks.filter((_, i) => i !== idx)
    set(next.length > 0 ? next : [{}])
  }

  return (
    <div>
      {trucks.map((truck, idx) => (
        <div key={idx} style={{ border: '1.5px solid #e2e8e5', borderRadius: 10, padding: '12px 14px', marginBottom: 10, background: idx % 2 === 0 ? '#fff' : 'rgba(30,61,48,0.02)' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
            <span style={{ fontSize: 12, fontWeight: 700, color: '#1e3d30' }}>Truck {idx + 1}</span>
            {trucks.length > 1 && (
              <button onClick={() => removeTruck(idx)} style={{ fontSize: 11, color: '#dc3545', background: 'none', border: 'none', cursor: 'pointer', fontWeight: 600, fontFamily: 'var(--font)' }}>✕ Remove</button>
            )}
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 10 }}>
            {subFields.map(sf => (
              <div key={sf.key}>
                <label style={{ fontSize: 11, fontWeight: 600, color: '#6b8278', display: 'block', marginBottom: 4 }}>{sf.label}</label>
                {sf.type === 'currency' ? (
                  <div style={{ position: 'relative' }}>
                    <span style={{ position: 'absolute', left: 10, top: '50%', transform: 'translateY(-50%)', fontSize: 12, color: '#6b8278', fontWeight: 600, pointerEvents: 'none' }}>$</span>
                    <input className="ff-input" style={{ width: '100%', paddingLeft: 20, ...highlightStyle }} type="text" inputMode="decimal" value={truck[sf.key] || ''} onChange={e => updateTruck(idx, sf.key, e.target.value.replace(/[^\d.,]/g, ''))} />
                  </div>
                ) : (
                  <input className="ff-input" style={{ width: '100%', ...highlightStyle }} type="text" value={truck[sf.key] || ''} onChange={e => updateTruck(idx, sf.key, e.target.value)} />
                )}
              </div>
            ))}
          </div>
        </div>
      ))}
      <button onClick={addTruck} style={{ padding: '8px 14px', borderRadius: 8, border: '1.5px dashed #1e3d30', background: 'rgba(30,61,48,0.04)', color: '#1e3d30', fontSize: 12, fontWeight: 700, cursor: 'pointer', fontFamily: 'var(--font)' }}>
        + Add Another Truck
      </button>
    </div>
  )
}

// ── Progress calculation per section ──────────────────────────────────────────
function hasValue(v) {
  return Array.isArray(v) ? v.length > 0 : (v !== undefined && v !== null && v !== '')
}

// A field counts as filled if it has its own data, OR — for fields that replaced an
// older field (via migratesFrom) — if any of the old fields it superseded were answered.
// This stops previously-completed fact finds from permanently reading <100% just
// because a schema change introduced a new field key for the same information.
function isFieldFilled(f, ffData) {
  if (hasValue(ffData[f.key])) return true
  if (f.migratesFrom) return f.migratesFrom.some(k => hasValue(ffData[k]))
  return false
}

function calcSectionProgress(section, ffData) {
  const visibleFields = section.fields.filter(f => {
    if (f.showIf && ffData[f.showIf.key] !== f.showIf.value) return false
    if (f.legacy && !hasValue(ffData[f.key])) return false
    return true
  })
  if (visibleFields.length === 0) return { filled: 0, total: 0, pct: 100 }
  const filled = visibleFields.filter(f => isFieldFilled(f, ffData)).length
  return { filled, total: visibleFields.length, pct: Math.round((filled / visibleFields.length) * 100) }
}

// ── One fact find panel (used once for single view, twice for compare view) ──
function FactFindPanel({ policyType, ffData, onChange, brokerName, highlightKeys, title, lastSavedAt, dirty }) {
  const factFind = FACT_FINDS[policyType]
  if (!factFind) return <div style={{ padding: 24, color: '#888' }}>No fact find for: {policyType}</div>

  const sectionProgress = factFind.sections.map(s => calcSectionProgress(s, ffData))
  const totalFilled = sectionProgress.reduce((sum, s) => sum + s.filled, 0)
  const totalFields = sectionProgress.reduce((sum, s) => sum + s.total, 0)
  const overallPct = totalFields === 0 ? 0 : Math.round((totalFilled / totalFields) * 100)

  const saveLabel = useMemo(() => {
    if (dirty) return 'Saving...'
    if (!lastSavedAt) return 'Not saved yet'
    const s = Math.max(0, Math.round((Date.now() - lastSavedAt) / 1000))
    if (s < 5) return 'Saved just now'
    if (s < 60) return `Saved ${s}s ago`
    const m = Math.round(s / 60)
    return `Saved ${m}m ago`
  }, [lastSavedAt, dirty])

  const renderField = (field) => {
    if (field.showIf && ffData[field.showIf.key] !== field.showIf.value) return null

    const val = ffData[field.key]
    const set = (v) => onChange(prev => ({ ...prev, [field.key]: v }))
    const isHighlighted = highlightKeys?.has(field.key)
    const highlightStyle = isHighlighted ? { border: '2px solid #e6a817', borderRadius: 8, outline: 'none' } : {}
    const isAbn = field.key === 'ff_abn'
    const isAddress = ADDRESS_FIELD_KEYS.has(field.key)

    const isFullWidth =
      field.type === 'textarea' ||
      isAddress ||
      isAbn ||
      (field.label && field.label.length > 80) ||
      field.type === 'checkboxgroup'

    return (
      <div
        key={field.key}
        className="ff-field"
        style={{
          gridColumn: isFullWidth ? '1 / -1' : 'auto',
          marginBottom: 0,
          padding: isHighlighted ? '8px 10px' : 0,
          borderRadius: isHighlighted ? 8 : 0,
          background: isHighlighted ? 'rgba(230,168,23,0.06)' : 'transparent',
          border: isHighlighted ? '2px solid #e6a817' : 'none',
          transition: 'all .2s ease'
        }}
      >
        <label className="ff-label" style={{ color: isHighlighted ? '#7a5500' : undefined }}>
          {field.label}
          {isHighlighted && <span style={{ fontSize: 10, fontWeight: 700, color: '#c05c00', marginLeft: 6, background: 'rgba(230,168,23,0.18)', padding: '1px 6px', borderRadius: 4 }}>Required</span>}
        </label>

        {isAbn ? (
          <AbnField val={val || ''} set={set} onChange={onChange} highlightStyle={highlightStyle} />
        ) : isAddress ? (
          <AddressField val={val} set={set} placeholder={field.placeholder} highlightStyle={highlightStyle} />
        ) : field.type === 'textarea' ? (
          <textarea className="ff-input" style={{ width: '100%', ...highlightStyle }} value={val || ''} onChange={e => set(e.target.value)} placeholder={field.placeholder || ''} />
        ) : field.type === 'select' ? (
          <select className="ff-input" style={{ width: '100%', ...highlightStyle }} value={val || ''} onChange={e => set(e.target.value)}>
            <option value="">Select...</option>
            {(field.options || []).map(o => <option key={o}>{o}</option>)}
          </select>
        ) : field.type === 'radio' ? (
          <div className="ff-radio-group" style={isHighlighted ? { padding: '6px 8px', borderRadius: 6, border: '2px solid #e6a817' } : {}}>
            {(field.options || []).map(o => (
              <label
                key={o}
                className="ff-radio-label"
                style={{
                  borderColor: val === o ? '#1e3d30' : undefined,
                  background: val === o ? 'rgba(30,61,48,0.08)' : undefined,
                  color: val === o ? '#1e3d30' : undefined,
                  fontWeight: val === o ? 600 : undefined,
                }}
              >
                <input type="radio" name={`${field.key}-${policyType}`} value={o} checked={val === o} onChange={() => set(o)} style={{ display: 'none' }} />
                {o}
              </label>
            ))}
          </div>
        ) : field.type === 'checkboxgroup' ? (
          <div className="ff-checkbox-group" style={isHighlighted ? { padding: '6px 8px', borderRadius: 6, border: '2px solid #e6a817' } : {}}>
            {(field.options || []).map(o => {
              const checked = (val || []).includes(o)
              return (
                <label
                  key={o}
                  className="ff-checkbox-label"
                  style={{
                    borderColor: checked ? '#1e3d30' : undefined,
                    background: checked ? 'rgba(30,61,48,0.08)' : undefined,
                    color: checked ? '#1e3d30' : undefined,
                    fontWeight: checked ? 600 : undefined,
                  }}
                >
                  <input type="checkbox" checked={checked} onChange={e => { const current = val || []; set(e.target.checked ? [...current, o] : current.filter(x => x !== o)) }} style={{ display: 'none' }} />
                  {o}
                </label>
              )
            })}
          </div>
        ) : field.type === 'currency' ? (
          <div style={{ position: 'relative' }}>
            <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#6b8278', fontWeight: 600, pointerEvents: 'none' }}>$</span>
            <input
              className="ff-input"
              style={{ width: '100%', paddingLeft: 24, ...highlightStyle }}
              type="text"
              inputMode="decimal"
              value={val || ''}
              onChange={e => set(e.target.value.replace(/[^\d.,]/g, ''))}
              placeholder={field.placeholder || '0'}
            />
          </div>
        ) : field.type === 'date' ? (
          <input className="ff-input" style={{ width: '100%', ...highlightStyle }} type="date" value={val || ''} onChange={e => set(e.target.value)} />
        ) : (
          <input
            className="ff-input"
            style={{ width: '100%', ...highlightStyle }}
            type={field.type === 'number' ? 'number' : 'text'}
            value={val || ''}
            onChange={e => set(e.target.value)}
            placeholder={field.placeholder || ''}
          />
        )}
      </div>
    )
  }

return (
    <div style={{ fontFamily: "'DM Sans', sans-serif" }}>
      {/* Benchmark header */}
      <div style={{
        background: '#1e3d30',
        borderRadius: '12px 12px 0 0',
        padding: '16px 20px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        marginBottom: 0,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
          <div style={{ width: 36, height: 36, borderRadius: 8, background: 'rgba(255,255,255,0.15)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 16, fontWeight: 800, color: '#fff' }}>B</div>
          <div>
            <div style={{ fontSize: 14, fontWeight: 800, color: '#fff', letterSpacing: '-0.2px' }}>Benchmark Insurance Brokers</div>
            <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', marginTop: 1 }}>{factFind.label || policyType} — Fact Find</div>
          </div>
        </div>
        {brokerName && (
          <div style={{ fontSize: 11, color: 'rgba(255,255,255,0.65)', textAlign: 'right' }}>
            <div style={{ fontWeight: 600, color: 'rgba(255,255,255,0.9)' }}>{brokerName}</div>
            <div>Benchmark Insurance Brokers</div>
          </div>
        )}
      </div>
      {/* Sticky header with progress + save indicator */}
      <div style={{
        position: 'sticky',
        top: 0,
        zIndex: 10,
background: '#fff',
        border: '1.5px solid #e2e8e5',
        borderRadius: 0,
        padding: '12px 16px',
        marginBottom: 14,
        boxShadow: '0 2px 8px rgba(0,0,0,0.04)',
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12 }}>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 600, color: '#1a2e25' }}>
              {title || factFind.label || policyType}
            </div>
            <div style={{ fontSize: 11, color: '#6b8278', marginTop: 2 }}>
              {totalFilled} / {totalFields} fields · {overallPct}% complete
            </div>
          </div>
          <div style={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 6,
            fontSize: 11,
            padding: '4px 10px',
            borderRadius: 20,
            background: dirty ? 'rgba(230,168,23,0.12)' : 'rgba(30,61,48,0.08)',
            color: dirty ? '#7a5500' : '#1e3d30',
            fontWeight: 600,
            whiteSpace: 'nowrap',
            flexShrink: 0,
          }}>
            <span style={{
              width: 6,
              height: 6,
              borderRadius: '50%',
              background: dirty ? '#e6a817' : '#1e3d30',
            }} />
            {saveLabel}
          </div>
        </div>
        {/* Overall progress bar */}
        <div style={{ height: 4, background: '#f0f4f2', borderRadius: 2, overflow: 'hidden', marginTop: 10 }}>
          <div style={{ height: '100%', background: 'linear-gradient(90deg, #1e3d30, #f26522)', borderRadius: 2, width: `${overallPct}%`, transition: 'width .4s ease' }} />
        </div>
      </div>

      {/* Sections */}
      {factFind.sections.map((section, idx) => {
        const progress = sectionProgress[idx]
        const isDone = progress.total > 0 && progress.filled === progress.total

        return (
          <div
            key={idx}
            style={{
              background: '#fff',
              border: '1.5px solid #e2e8e5',
              borderRadius: 12,
              marginBottom: 10,
              overflow: 'hidden',
            }}
          >
            {/* Section header (always visible, non-interactive) */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                padding: '14px 18px',
                userSelect: 'none',
                gap: 12,
                background: 'rgba(30,61,48,0.03)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 10, flex: 1, minWidth: 0 }}>
                <div style={{
                  width: 24,
                  height: 24,
                  borderRadius: '50%',
                  background: isDone ? '#1e3d30' : 'rgba(30,61,48,0.08)',
                  color: isDone ? '#fff' : '#6b8278',
                  fontSize: 11,
                  fontWeight: 700,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  flexShrink: 0,
                }}>
                  {isDone ? '✓' : idx + 1}
                </div>
                <div style={{ fontSize: 14, fontWeight: 600, color: '#1a2e25' }}>
                  {section.title}
                </div>
              </div>
              <span style={{
                fontSize: 11,
                fontWeight: 600,
                color: isDone ? '#1e3d30' : '#6b8278',
                padding: '2px 8px',
                borderRadius: 10,
                background: isDone ? 'rgba(30,61,48,0.1)' : 'rgba(107,130,120,0.1)',
                flexShrink: 0,
              }}>
                {progress.filled} / {progress.total}
              </span>
            </div>

          {/* Section body — always rendered */}
            <div style={{ borderTop: '1.5px solid #e2e8e5' }}>
              {section.warning && (
                <div style={{
                  background: '#fff8f0',
                  border: '1.5px solid #f26522',
                  borderLeft: '4px solid #f26522',
                  borderRadius: 6,
                  padding: '10px 14px',
                  fontSize: 12,
                  color: '#c0450a',
                  margin: '12px 18px',
                  lineHeight: 1.6,
                }}>
                  {section.warning}
                </div>
              )}
{section.fields.map((field, fi) => {
                if (field.showIf && ffData[field.showIf.key] !== field.showIf.value) return null
                if (field.legacy) {
                  const lv = ffData[field.key]
                  const hasData = Array.isArray(lv) ? lv.length > 0 : (lv !== undefined && lv !== null && lv !== '')
                  if (!hasData) return null
                }
                if (field.legacy) {
                  const lv = ffData[field.key]
                  const hasData = Array.isArray(lv) ? lv.length > 0 : (lv !== undefined && lv !== null && lv !== '')
                  if (!hasData) return null
                }
                const val = ffData[field.key]
                const set = (v) => onChange(prev => ({ ...prev, [field.key]: v }))
                const isHighlighted = highlightKeys?.has(field.key)
                const highlightStyle = isHighlighted ? { border: '2px solid #e6a817', borderRadius: 8, outline: 'none' } : {}
                const isAbn = field.key === 'ff_abn'
                const isAddress = ADDRESS_FIELD_KEYS.has(field.key)
                const isWide = field.type === 'textarea' || isAddress || isAbn || field.type === 'checkboxgroup' || field.type === 'truckgroup' || (field.label && field.label.length > 80)

                return (
<div key={field.key} style={{
                    display: 'grid',
                    gridTemplateColumns: isWide ? '1fr' : '240px 1fr',
                    gap: isWide ? 6 : 16,
                    padding: '12px 18px',
                    borderBottom: fi < section.fields.length - 1 ? '1px solid #f0f4f2' : 'none',
                    background: isHighlighted ? 'rgba(230,168,23,0.08)' : fi % 2 === 0 ? '#fff' : 'rgba(30,61,48,0.012)',
                    alignItems: 'start',
                    borderLeft: isHighlighted ? '4px solid #e6a817' : '4px solid transparent',
                    transition: 'all .2s',
                  }}>
                    <label style={{
                      fontSize: 13,
                      fontWeight: 600,
                      color: isHighlighted ? '#7a5500' : '#4a5e55',
                      lineHeight: 1.4,
                      paddingTop: isWide ? 0 : 8,
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: 6,
                    }}>
                      {field.label}
                      {isHighlighted && (
                        <span style={{ fontSize: 10, fontWeight: 700, color: '#c05c00', background: 'rgba(230,168,23,0.18)', padding: '1px 6px', borderRadius: 4, whiteSpace: 'nowrap', marginTop: 2 }}>Required</span>
                      )}
                    </label>

                    <div>
                      {isAbn ? (
                        <AbnField val={val || ''} set={set} onChange={onChange} highlightStyle={highlightStyle} />
                      ) : isAddress ? (
                        <AddressField val={val} set={set} placeholder={field.placeholder} highlightStyle={highlightStyle} />
                      ) : field.type === 'textarea' ? (
                        <textarea className="ff-input" style={{ width: '100%', minHeight: 80, ...highlightStyle }} value={val || ''} onChange={e => set(e.target.value)} placeholder={field.placeholder || ''} />
                      ) : field.type === 'select' ? (
                        <select className="ff-input" style={{ width: '100%', ...highlightStyle }} value={val || ''} onChange={e => set(e.target.value)}>
                          <option value="">Select...</option>
                          {(field.options || []).map(o => <option key={o}>{o}</option>)}
                        </select>
                      ) : field.type === 'radio' ? (
                        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', paddingTop: 4 }}>
                          {(field.options || []).map(o => (
                            <label key={o} style={{
                              display: 'inline-flex', alignItems: 'center', gap: 6,
                              padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                              border: `1.5px solid ${val === o ? '#1e3d30' : '#e2e8e5'}`,
                              background: val === o ? 'rgba(30,61,48,0.08)' : '#fff',
                              color: val === o ? '#1e3d30' : '#6b8278',
                              fontWeight: val === o ? 700 : 400,
                              fontSize: 13, transition: 'all .15s',
                              ...(isHighlighted && val !== o ? { borderColor: '#e6a817' } : {}),
                            }}>
                              <input type="radio" name={`${field.key}-${policyType}`} value={o} checked={val === o} onChange={() => set(o)} style={{ display: 'none' }} />
                              {o}
                            </label>
                          ))}
                        </div>
                      ) : field.type === 'checkboxgroup' ? (
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, paddingTop: 4 }}>
                          {(field.options || []).map(o => {
                            const checked = (val || []).includes(o)
                            return (
                              <label key={o} style={{
                                display: 'inline-flex', alignItems: 'center', gap: 6,
                                padding: '6px 14px', borderRadius: 8, cursor: 'pointer',
                                border: `1.5px solid ${checked ? '#1e3d30' : '#e2e8e5'}`,
                                background: checked ? 'rgba(30,61,48,0.08)' : '#fff',
                                color: checked ? '#1e3d30' : '#6b8278',
                                fontWeight: checked ? 700 : 400,
                                fontSize: 13, transition: 'all .15s',
                              }}>
                                <input type="checkbox" checked={checked} onChange={e => { const current = val || []; set(e.target.checked ? [...current, o] : current.filter(x => x !== o)) }} style={{ display: 'none' }} />
                                {checked ? '✓ ' : ''}{o}
                              </label>
                            )
                          })}
                        </div>
                      ) : field.type === 'truckgroup' ? (
                        <TruckGroupField val={val} set={set} subFields={field.subFields} highlightStyle={highlightStyle} />
                      ) : field.type === 'currency' ? (
                        <div style={{ position: 'relative', maxWidth: 240 }}>
                          <span style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', fontSize: 13, color: '#6b8278', fontWeight: 600, pointerEvents: 'none' }}>$</span>
                          <input className="ff-input" style={{ width: '100%', paddingLeft: 24, ...highlightStyle }} type="text" inputMode="decimal" value={val || ''} onChange={e => set(e.target.value.replace(/[^\d.,]/g, ''))} placeholder={field.placeholder || '0'} />
                        </div>
                      ) : field.type === 'date' ? (
                        <input className="ff-input" style={{ maxWidth: 200, ...highlightStyle }} type="date" value={val || ''} onChange={e => set(e.target.value)} />
                      ) : field.type === 'number' ? (
                        <input className="ff-input" style={{ maxWidth: 160, ...highlightStyle }} type="number" value={val || ''} onChange={e => set(e.target.value)} placeholder={field.placeholder || ''} />
                      ) : (
                        <input className="ff-input" style={{ width: '100%', ...highlightStyle }} type="text" value={val || ''} onChange={e => set(e.target.value)} placeholder={field.placeholder || ''} />
                      )}
                    </div>
                  </div>
                )
              })}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Main exported component — handles single view + compare view + auto-save ─
export function FactFindSection({
  policyType,
  ffData,
  onChange,
  brokerName,
  highlightKeys,
  // Optional new props for compare mode — backwards compatible
  allFactFinds,       // e.g. { 'Truck': {...}, 'Public Liability': {...} } — for compare dropdown
  compareWith,        // policy type string of the compared fact find
  setCompareWith,     // setter from parent — opens/closes compare mode
  compareData,        // answers object of the compared fact find
  compareOnChange,    // onChange for the compared fact find
  // Optional auto-save hook — parent can supply these to show "saved X ago"
  lastSavedAt,
  dirty,
}) {
  const factFind = FACT_FINDS[policyType]
  if (!factFind) return <div style={{ padding: 24, color: '#888' }}>No fact find for: {policyType}</div>

  // List of OTHER fact finds available to compare with (same client)
  const compareOptions = useMemo(() => {
    if (!allFactFinds) return []
    return Object.keys(allFactFinds).filter(pt => pt !== policyType)
  }, [allFactFinds, policyType])

  const isCompareMode = compareWith && compareData && compareOnChange

  return (
    <div>
      {/* Compare-with toolbar — only renders if parent passed allFactFinds */}
      {allFactFinds && compareOptions.length > 0 && (
        <div style={{
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          flexWrap: 'wrap',
          padding: '10px 14px',
          background: 'rgba(242,101,34,0.06)',
          border: '1.5px solid rgba(242,101,34,0.2)',
          borderRadius: 10,
          marginBottom: 14,
        }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: '#c04a0a' }}>
            {isCompareMode ? '🔍 Compare mode' : '💡 Cross-sell tip'}
          </span>
          {isCompareMode ? (
            <>
              <span style={{ fontSize: 12, color: '#6b8278' }}>
                Viewing <strong>{factFind.label || policyType}</strong> alongside <strong>{FACT_FINDS[compareWith]?.label || compareWith}</strong>
              </span>
              <button
                onClick={() => setCompareWith(null)}
                style={{ marginLeft: 'auto', padding: '5px 12px', borderRadius: 6, border: '1.5px solid #6b8278', background: '#fff', color: '#6b8278', fontSize: 11, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
              >
                ✕ Close compare
              </button>
            </>
          ) : (
            <>
              <span style={{ fontSize: 12, color: '#6b8278' }}>
                Compare this fact find with another to cross-sell:
              </span>
              <select
                onChange={e => e.target.value && setCompareWith(e.target.value)}
                value=""
                style={{ padding: '5px 10px', borderRadius: 6, border: '1.5px solid #e2e8e5', background: '#fff', fontSize: 12, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--font)' }}
              >
                <option value="">Select a fact find to compare...</option>
                {compareOptions.map(pt => (
                  <option key={pt} value={pt}>{FACT_FINDS[pt]?.label || pt}</option>
                ))}
              </select>
            </>
          )}
        </div>
      )}

      {/* Main panel + optional compare panel */}
      <div style={{
        display: 'grid',
        gridTemplateColumns: isCompareMode ? 'repeat(auto-fit, minmax(420px, 1fr))' : '1fr',
        gap: 14,
        alignItems: 'flex-start',
      }}>
        <FactFindPanel
          policyType={policyType}
          ffData={ffData}
          onChange={onChange}
          brokerName={brokerName}
          highlightKeys={highlightKeys}
          lastSavedAt={lastSavedAt}
          dirty={dirty}
        />

        {isCompareMode && (
          <FactFindPanel
            policyType={compareWith}
            ffData={compareData}
            onChange={compareOnChange}
            brokerName={brokerName}
            highlightKeys={new Set()}
            title={FACT_FINDS[compareWith]?.label || compareWith}
          />
        )}
      </div>
    </div>
  )
}

export default FactFindSection
